import type { Transaction } from "@rocicorp/zero";
import * as z from "zod";
import { userFacingError } from "./authorization";
import { maxSpaceSlugLength, tenantEdgeUrlOptionsFromTenant } from "./edgeUrl";
import { type Schema, zql } from "./generatedDrizzleSchema";
import { namesMatch, normalizeName } from "./normalizeName";
import type { SpaceMode } from "./schema";
import { type Slug, nameSluggable, slugValidationError } from "./slug";
import { edgeUrlStackConfigFromEnv } from "./tenantEdgeUrlDerivation";

export const MAX_SPACE_NAME_LENGTH = 50;

export const spaceNameField = z
  .string()
  .max(MAX_SPACE_NAME_LENGTH, {
    message: `Space name must be ${MAX_SPACE_NAME_LENGTH} characters or less`,
  })
  .transform(normalizeName)
  .refine(nameSluggable, {
    message: "Space name must contain at least one letter or digit",
  });

export const spaceSlugField = z.string().superRefine((slug, ctx) => {
  const error = slugValidationError(slug);
  if (error) ctx.addIssue({ code: "custom", message: error });
});

export async function spaceSlugError(
  tx: Transaction<Schema>,
  tenantId: string,
  slug: string
): Promise<string | null> {
  if (tx.location !== "server") return slugValidationError(slug);
  const tenant = await tx.run(zql.tenants.where("id", tenantId).one());
  const options = tenantEdgeUrlOptionsFromTenant(
    {
      edgeOrigin: tenant!.publicOrigin,
      edgeWildcardDomain: tenant!.publicWildcardDomain,
    },
    edgeUrlStackConfigFromEnv()
  );
  return slugValidationError(slug, maxSpaceSlugLength(options));
}

export function displaySpaceName(
  space: { id: string; name: string; mode: SpaceMode },
  viewerPersonalSpaceId: string | null,
  ownerName?: string | null
): string {
  if (space.mode !== "personal") return space.name;
  if (space.id === viewerPersonalSpaceId) return "Personal workflows";
  const label = ownerName || space.name;
  return `${label}’s personal workflows`;
}

export function assertNameAvailable(
  existingSpaces: ReadonlyArray<{ id: string; name: string }>,
  name: string,
  excludeSpaceId?: string
): void {
  if (
    existingSpaces.some(
      (s) => s.id !== excludeSpaceId && namesMatch(s.name, name)
    )
  ) {
    throw userFacingError("A space with this name already exists");
  }
}

export function assertSlugAvailable(
  existingSpaces: ReadonlyArray<{ id: string; slug: string }>,
  slug: Slug,
  excludeSpaceId?: string
): void {
  if (existingSpaces.some((s) => s.id !== excludeSpaceId && s.slug === slug)) {
    throw userFacingError("This space URL is already taken");
  }
}

export function assertNotPersonal(
  space: { mode: SpaceMode },
  action: string
): void {
  if (space.mode === "personal") {
    throw userFacingError(`Cannot ${action} personal workflows`);
  }
}
